import { useMemo } from "react";
import { useClips, useTimezonedBounds } from "@visioni/a3s-media/app";
import { useCameraAlarmPresentation } from "@src/features/cameras/hooks/useCameraAlarmPresentation";
import { getCameraDisplayName, getCameraKeyString } from "@src/utils/key-parser";
import { usePeriodicRefresh } from "@src/hooks/usePeriodicRefresh";
import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import type { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import { useAppState } from "@src/hooks/useAppState";

interface UseCameraDetailsViewModelParams {
  camera: CameraApiResponse;
  alarmDetail?: AlarmConfiguration | null;
  siteId: string;
}

export function useCameraDetailsViewModel({ camera, alarmDetail, siteId }: UseCameraDetailsViewModelParams) {
  const cameraKey = getCameraKeyString(camera);
  const cameraDisplayName = camera.nickname || getCameraDisplayName(cameraKey) || cameraKey;
  const snapshotUrl = camera.snapshot?.url ?? null;
  const snapshotCapturedAt = camera.snapshot?.capturedAt ?? null;
  const isOnline = camera.isOnline;

  const currentTime = usePeriodicRefresh();
  const { isArmed, statusText, scheduleTime } = useCameraAlarmPresentation({ alarmDetail, currentTime });

  const appState = useAppState();
  const timezone = useMemo(
    () => getTimezoneFromAlarmDetail(alarmDetail) || Intl.DateTimeFormat().resolvedOptions().timeZone,
    [alarmDetail],
  );
  const { startTime, endTime } = useTimezonedBounds(timezone, appState);

  const { data: clipItems } = useClips({
    siteId: siteId as SiteId,
    assetCameraKey: cameraKey as AssetCameraKey,
    startTime,
    endTime,
    limit: 100,
  });

  const alertsCount = clipItems?.length ?? 0;

  const scheduleStatus = useMemo(() => {
    if (!alarmDetail?.settings?.routine) return "Not configured";
    return "Schedule operating";
  }, [alarmDetail]);

  return {
    cameraKey,
    cameraDisplayName,
    snapshotUrl,
    snapshotCapturedAt,
    isOnline,
    isArmed,
    statusText,
    scheduleTime,
    alertsCount,
    scheduleStatus,
    timezone,
  };
}
